/* eslint-disable @typescript-eslint/no-explicit-any */
import { hydrateComponentStateWithAppState } from "../utils/hydrate-component-state-with-app-state";
import { UniversalAppState } from "./app";
import { useAppState, ZustandAppState } from "./app-state-context";
import { EventEmitter } from "./event-emitter";

export interface ComponentProps<
  ComponentEvent,
  ComponentEventPayload = undefined,
> {
  fireEvent?: (event: ComponentEvent, payload?: ComponentEventPayload) => void;
}

/**
 * A React component that can be used as a Pimo component.
 */
export type PimoReactComponent<
  ComponentState,
  ComponentEventName = "",
  ComponentEventPayload = undefined,
> = React.FC<
  ComponentState & ComponentProps<ComponentEventName, ComponentEventPayload>
>;

export type ComponentDefinition<
  ComponentState = undefined,
  ComponentEventName = "",
  ComponentEventPayload = undefined,
  ComponentLayoutProps = object,
> = {
  component:
    | PimoReactComponent<
        ComponentState,
        ComponentEventName,
        ComponentEventPayload
      >
    | PimoClassComponent<
        ComponentState,
        ComponentEventName,
        ComponentEventPayload
      >;

  layoutProps?: ComponentLayoutProps;
};

export type ComponentEvent<ComponentEventName = string> = {
  eventName: ComponentEventName;
} & Record<string | number, unknown>;

export type DerivePimoReactComponentProps<Component> =
  Component extends PimoReactComponent<infer Props, any> ? Props : never;

export class Component<
  AppState,
  ComponentState,
  ComponentEventName,
  ComponentEventPayload = undefined,
  ComponentLayoutProps = object,
> extends EventEmitter<ComponentEventName, ComponentEventPayload> {
  stateMapper: (appState: AppState) => ComponentState = () =>
    ({}) as ComponentState;

  visibilityMapper?: (appState: AppState) => boolean;

  layoutPropsMapper?: (appState: AppState) => ComponentLayoutProps;

  private renderedComponent?: React.FC;
  public layoutProps?: ComponentLayoutProps;

  constructor(
    private readonly _definition: ComponentDefinition<
      ComponentState,
      ComponentEventName,
      ComponentEventPayload,
      ComponentLayoutProps
    >
  ) {
    super();
    this.layoutProps = _definition.layoutProps;
  }

  public render(): React.FC {
    const boundFireEvent = this.fireEvent.bind(this);
    if (!this.renderedComponent) {
      const ComponentToRender = this._getComponentToRender(
        this._definition.component
      );

      const RenderedComponent = () => {
        const appState: UniversalAppState = useAppState() as UniversalAppState;

        const componentState = useAppState(
          this.stateMapper as (state: ZustandAppState) => ComponentState
        );

        if (appState.experimentalFeatures?.componentStateHydration) {
          hydrateComponentStateWithAppState<ComponentState>(
            componentState,
            appState
          );
        }

        const isComponentVisible = this.visibilityMapper
          ? // eslint-disable-next-line react-hooks/rules-of-hooks
            useAppState(
              this.visibilityMapper as (state: ZustandAppState) => boolean
            )
          : true;

        if (!componentState || !isComponentVisible) {
          return null;
        }

        return (
          <ComponentToRender {...componentState} fireEvent={boundFireEvent} />
        );
      };
      this.renderedComponent = RenderedComponent;
    }

    return this.renderedComponent;
  }

  /**
   * Returns the actual React component to render, both for function and class Pimo components.
   */
  private _getComponentToRender(
    component:
      | PimoClassComponent
      | PimoReactComponent<
          ComponentState,
          ComponentEventName,
          ComponentEventPayload
        >
  ) {
    if (this._isClassComponent(component)) {
      return component.render();
    }

    return component;
  }

  private _isClassComponent(
    component:
      | PimoClassComponent
      | PimoReactComponent<
          ComponentState,
          ComponentEventName,
          ComponentEventPayload
        >
  ): component is PimoClassComponent {
    return (component as PimoClassComponent).render != null;
  }

  public mapState(mapper: (appState: AppState) => ComponentState) {
    this.stateMapper = mapper;
  }

  public mapVisibility(mapper: (appState: AppState) => boolean) {
    this.visibilityMapper = mapper;
  }

  public mapLayoutProps(mapper: (appState: AppState) => ComponentLayoutProps) {
    this.layoutPropsMapper = mapper;
  }
}

export abstract class PimoClassComponent<
  ComponentState = any,
  ComponentEventNames = any,
  ComponentEventPayload = any,
> {
  abstract render(): PimoReactComponent<
    ComponentState,
    ComponentEventNames,
    ComponentEventPayload
  >;
}

export type PimoComponent<
  ComponentState,
  ComponentEventName = "",
  ComponentEventPayload = unknown,
> =
  | PimoReactComponent<
      ComponentState,
      ComponentEventName,
      ComponentEventPayload
    >
  | PimoClassComponent<
      ComponentState,
      ComponentEventName,
      ComponentEventPayload
    >;
